"use client";

import { cn } from "@/lib/utils";
import { motion, useMotionValue, useTransform } from "framer-motion";
import React, { useCallback, useEffect, useRef, useState } from "react";

interface RangeSliderProps {
    min: number;
    max: number;
    value: number;
    onChange: (value: number) => void;
    snap?: boolean;
    label?: string;
    className?: string;
}

export default function RangeSlider({ min, max, value, onChange, snap = false, label, className }: RangeSliderProps) {
    const trackRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    const steps = max - min;
    const fraction = (value - min) / (max - min);
    const pct = fraction * 100;

    const rawPercent = useMotionValue(pct);

    const syncToValue = useCallback(() => {
        if (isDragging || snap) return;
        rawPercent.set(pct);
    }, [pct, isDragging, snap, rawPercent]);

    useEffect(() => {
        syncToValue();
    }, [syncToValue]);

    const freeLeft = useTransform(rawPercent, (p) => `${p}%`);
    const freeWidth = useTransform(rawPercent, (p) => `${p}%`);

    const resolveValue = (clientX: number) => {
        if (!trackRef.current) return;
        const rect = trackRef.current.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

        if (snap) {
            const snapped = Math.round(ratio * steps);
            onChange(min + snapped);
        } else {
            const raw = min + ratio * (max - min);
            onChange(Math.round(raw * 10) / 10);
            rawPercent.set(ratio * 100);
        }
    };

    const handlePointerDown = (e: React.PointerEvent) => {
        e.preventDefault();
        setIsDragging(true);
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        resolveValue(e.clientX);
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!isDragging) return;
        resolveValue(e.clientX);
    };

    const handlePointerUp = () => {
        setIsDragging(false);
    };

    const tickCount = snap ? steps + 1 : 0;

    const springTransition = { type: "spring" as const, stiffness: 800, damping: 40 };

    return (
        <div className={cn("flex flex-col gap-2 w-full select-none", className)}>
            {label && (
                <div className="flex justify-between items-center">
                    <span className="text-xs text-neutral-500">{label}</span>
                  
                </div>
            )}
            <div
                ref={trackRef}
                className="relative h-6 flex items-center cursor-pointer touch-none"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
            >
                <div className="absolute left-0 right-0 h-1 bg-neutral-200 rounded-full" />

                {snap ? (
                    <motion.div
                        className="absolute left-0 h-1 bg-primary rounded-full"
                        animate={{ width: `${pct}%` }}
                        transition={springTransition}
                    />
                ) : (
                    <motion.div
                        className="absolute left-0 h-1 bg-primary rounded-full"
                        style={{ width: freeWidth }}
                    />
                )}

                {snap && tickCount > 0 && (
                    <div className="absolute left-0 right-0 flex justify-between pointer-events-none">
                        {Array.from({ length: tickCount }, (_, i) => (
                            <div
                                key={i}
                                className={cn(
                                    "w-1 h-1 rounded-full",
                                    i <= Math.round(fraction * steps)
                                        ? "bg-primary"
                                        : "bg-neutral-300"
                                )}
                            />
                        ))}
                    </div>
                )}

                {snap ? (
                    <motion.div
                        className={cn(
                            "absolute w-3.5 h-3.5 rounded-full bg-white border border-primary shadow-sm pointer-events-none",
                            isDragging && "bg-primary shadow-md"
                        )}
                        animate={{ left: `${pct}%` }}
                        transition={springTransition}
                        style={{ translateX: "-50%" }}
                    />
                ) : (
                    <motion.div
                        className={cn(
                            "absolute w-3.5 h-3.5 rounded-full bg-white border border-primary shadow-sm pointer-events-none",
                            isDragging && "bg-primary shadow-md"
                        )}
                        style={{ left: freeLeft, translateX: "-50%" }}
                    />
                )}
            </div>

            {snap && (
                <div className="flex justify-between px-0.5">
                    {Array.from({ length: tickCount }, (_, i) => (
                        <span key={i} className="text-[10px] text-neutral-400 w-0 text-center">
                            {min + i}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
